import { HttpErrorResponse } from '@angular/common/http';
import {Component,DestroyRef,OnInit,computed,inject,input,output} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {AbstractControl,FormControl,FormGroup,ReactiveFormsModule,Validators} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import {catchError,debounceTime,distinctUntilChanged,finalize,map,startWith,switchMap,tap} from 'rxjs/operators';
import { AppState } from 'src/app/@store/app.reducers';
import {ActivarLoadingAction,DesactivarLoadingAction} from 'src/app/@store/actions';
import {IResponseValidaContrasena} from 'src/app/models/administracion-usuarios/administracion-usuarios.model';
import {INormaModelInput,IResponseModel} from 'src/app/models/seguros-atlas/atlas.model';
import { AdministrarUsuariosService } from 'src/app/services/administracion-usuarios/administrar-usuarios.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import ValidationRegistro from '../../validators/registro.validators';
import { RegistroInformativoDialogComponent } from '../dialogs/registro-informativo-dialog/registro-informativo-dialog.component';
import { RegistroValidadorDosFaComponent } from '../dialogs/registro-validador-dos-fa/registro-validador-dos-fa.component';

interface CatalogoPregunta {
  Text: string;
  Value: string | number;
}

interface LadaTelefonicaInternacionalResponse {
  valor_dato: number | string;
  descripl_dato: string;
}

interface CodigoPaisTelefono {
  codigo: string;
  pais: string;
  bandera: string;
}

type ContactoControlName = 'Email' | 'Telefono';
type TipoPersona = 1 | 2;

export type TipoRegistroProveedorPromotor = 'Proveedor' | 'Promotor';

interface RegistroProveedorPromotorPayload {
  NIP: string;
  Usuario: string;
  Cuenta: string | null;
  Password: string;
  ConfirmaPassword: string;
  Alias: string;
  Telefono: string | null;
  CodTelefono: string | null;
  Email: string;
  CodEmail: string | null;
  IdPregunta: string | number | null;
  Respuesta: string;
  TipoPersona: TipoPersona;
  Nombre: string;
  Paterno: string;
  Materno: string;
  RazonSocial: string;
  RFC: string;
  ValidarUsuario: string;
}

interface RegistroProveedorPromotorConfig {
  perfilNombre: string;
  etiquetaCuenta: string;
  placeholderCuenta: string;
  cuentaSoloNumerica: boolean;
}

type EstadoValidacionUsuario =
  | 'vacio'
  | 'invalido-local'
  | 'valido'
  | 'existente'
  | 'invalido-remoto';

interface ResultadoValidacionUsuario {
  estado: EstadoValidacionUsuario;
  mensaje?: string;
  error?: unknown;
}

type EstadoValidacionPassword = 'vacio' | 'valido' | 'invalido' | 'error';

interface ResultadoValidacionPassword {
  estado: EstadoValidacionPassword;
  respuesta?: IResponseValidaContrasena;
  error?: unknown;
}

const REGISTRO_PROVEEDOR_PROMOTOR_CONFIG: Record<
  TipoRegistroProveedorPromotor,
  RegistroProveedorPromotorConfig
> = {
  Proveedor: {
    perfilNombre: 'Proveedor',
    etiquetaCuenta: 'Clave del Proveedor (IDU)',
    placeholderCuenta: 'Ingresa tu clave de proveedor',
    cuentaSoloNumerica: true
  },
  Promotor: {
    perfilNombre: 'Promotor',
    etiquetaCuenta: 'Clave de Promotor',
    placeholderCuenta: 'Ingresa tu clave de promotor',
    cuentaSoloNumerica: false
  }
};

@Component({
  selector: 'app-registro-proveedor-promotor-form',
  standalone: true,
  templateUrl: './registro-proveedor-promotor-form.component.html',
  styleUrls: ['./registro-proveedor-promotor-form.component.scss'],
  imports: [ReactiveFormsModule, MatIcon]
})
export class RegistroProveedorPromotorFormComponent implements OnInit {
  readonly tipoRegistro = input<TipoRegistroProveedorPromotor>('Proveedor');
  readonly regresarPerfil = output<void>();
  readonly registroCompletado = output<string>();
  readonly formularioModificado = output<boolean>();

  readonly configuracionRegistro = computed(
    () => REGISTRO_PROVEEDOR_PROMOTOR_CONFIG[this.tipoRegistro()]
  );

  readonly tiposPersona: ReadonlyArray<{ Text: string; Value: TipoPersona }> = [
    { Text: 'Física', Value: 1 },
    { Text: 'Moral', Value: 2 }
  ];

  readonly todoFormulario = new FormGroup({
    NIP: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Usuario: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9_]*$/),
        Validators.minLength(4),
        Validators.maxLength(8)
      ]
    }),
    Cuenta: new FormControl<string | null>(null, [Validators.required]),
    Password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    ConfirmaPassword: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Alias: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Telefono: new FormControl<string | null>(null, [
      Validators.required,
      Validators.pattern('^[0-9]{11,13}$')
    ]),
    CodTelefono: new FormControl<string | null>(null),
    Email: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.email,
        Validators.pattern('[^@]+@[^@]+\\.[a-zA-Z]{2,}$')
      ]
    }),
    CodEmail: new FormControl<string | null>(null),
    IdPregunta: new FormControl<string | number | null>(null, [Validators.required]),
    Respuesta: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    TipoPersona: new FormControl<TipoPersona>(1, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Nombre: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Paterno: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    Materno: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    RazonSocial: new FormControl('', { nonNullable: true }),
    RFC: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(
          '^([A-Za-zÑñ\\x26]{3,4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Za-z0-9]{3})?$'
        ),
        Validators.maxLength(13)
      ]
    }),
    ValidarUsuario: new FormControl('false', {
      nonNullable: true,
      validators: [Validators.required]
    })
  }, {
    validators: ValidationRegistro.match('Password', 'ConfirmaPassword')
  });

  hidePassword = true;
  hideConfirmPassword = true;

  validandoCorreo = false;
  validandoTelefono = false;
  procesandoCorreo = false;
  procesandoTelefono = false;
  dosFaCorreoHabilitado = true;
  dosFaTelefonoHabilitado = true;
  consultandoNormaCorreo = true;
  consultandoNormaTelefono = true;
  twoFaCorreoAbierto = false;
  twoFaTelefonoAbierto = false;
  loadingRegistro = false;

  emailValidado = '';
  telefonoValidado = '';
  actualizarUsuario = 'false';

  validandoPassword = false;
  passwordValido = false;
  generaErrorPasswordForm = false;
  passwordValidaciones: string[] = [];

  usuarioValido = {
    is: false,
    pending: false
  };

  validacionUsuario = '';
  catPreguntas: CatalogoPregunta[] = [];

  codigosPaisTelefono: CodigoPaisTelefono[] = [
    { codigo: '52', pais: 'México', bandera: 'MX' }
  ];

  codigoPaisTelefonoSeleccionado: CodigoPaisTelefono = this.codigosPaisTelefono[0];
  telefonoLocal = '';

  errorUsuarioContacto: Record<ContactoControlName, string> = {
    Email: '',
    Telefono: ''
  };

  private confirmandoCambioCorreo = false;
  private confirmandoCambioTelefono = false;
  private tipoPersonaAnterior: TipoPersona | null = null;
  private normaCorreoConsultada = false;
  private normaTelefonoConsultada = false;
  private consultasNormasPendientes = 0;

  private readonly authService = inject(AuthService);
  private readonly adminService = inject(AdministrarUsuariosService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.cargarConfiguracionDosFa();
    this.configurarValidadoresCuenta();
    this.configurarTipoPersona();
    this.configurarValidacionUsuario();
    this.configurarValidacionPassword();
    this.configurarSincronizacionConfirmacionPassword();
    this.configurarResetValidacionContacto();
    this.cargarCatalogoPreguntas();
    this.cargarLadasTelefonicasInternacionales();
  }

  regresar(): void {
    this.regresarPerfil.emit();
  }

  private cargarConfiguracionDosFa(): void {
    this.consultasNormasPendientes = 2;
    this.store.dispatch(ActivarLoadingAction());

    this.consultarConfiguracionDosFaCorreo();
    this.consultarConfiguracionDosFaTelefono();
  }

  private finalizarCargaInicialNorma(): void {
    this.consultasNormasPendientes = Math.max(
      0,
      this.consultasNormasPendientes - 1
    );

    if (this.consultasNormasPendientes === 0) {
      this.store.dispatch(DesactivarLoadingAction());
    }
  }

  private consultarConfiguracionDosFaCorreo(): void {
    this.consultandoNormaCorreo = true;

    this.authService.obtenerNormaAtlas(this.crearNorma(763))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.consultandoNormaCorreo = false;
          this.finalizarCargaInicialNorma();
        })
      )
      .subscribe({
        next: (response: IResponseModel) => {
          this.aplicarConfiguracionDosFaCorreo(response);
        },
        error: () => {
          this.normaCorreoConsultada = false;
          this.dosFaCorreoHabilitado = true;
        }
      });
  }

  private consultarConfiguracionDosFaTelefono(): void {
    this.consultandoNormaTelefono = true;

    this.authService.obtenerNormaAtlas(this.crearNorma(765))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.consultandoNormaTelefono = false;
          this.finalizarCargaInicialNorma();
        })
      )
      .subscribe({
        next: (response: IResponseModel) => {
          this.aplicarConfiguracionDosFaTelefono(response);
        },
        error: () => {
          this.normaTelefonoConsultada = false;
          this.dosFaTelefonoHabilitado = true;
        }
      });
  }

  private aplicarConfiguracionDosFaCorreo(response: IResponseModel): void {
    this.normaCorreoConsultada = true;
    this.dosFaCorreoHabilitado = response?.Respuesta?.Status == 1;

    if (!this.dosFaCorreoHabilitado) {
      this.todoFormulario.controls.CodEmail.setValue('9000');
      this.validandoCorreo = false;
      this.emailValidado = '';
      return;
    }

    if (this.todoFormulario.controls.CodEmail.value === '9000') {
      this.todoFormulario.controls.CodEmail.setValue('');
    }
  }

  private aplicarConfiguracionDosFaTelefono(response: IResponseModel): void {
    this.normaTelefonoConsultada = true;
    this.dosFaTelefonoHabilitado = response?.Respuesta?.Status == 1;

    if (!this.dosFaTelefonoHabilitado) {
      this.todoFormulario.controls.CodTelefono.setValue('9000');
      this.validandoTelefono = false;
      this.telefonoValidado = '';
      return;
    }

    if (this.todoFormulario.controls.CodTelefono.value === '9000') {
      this.todoFormulario.controls.CodTelefono.setValue('');
    }
  }

  private crearNorma(numeroNorma: number): INormaModelInput {
    return {
      NumNorma: numeroNorma,
      Parametro1: 1,
      Parametro2: 0,
      Parametro3: 0
    };
  }

  continuar(): void {
    this.todoFormulario.markAllAsTouched();

    if (!this.usuarioValido.is) {
      const usuarioControl = this.todoFormulario.controls.Usuario;

      usuarioControl.setErrors({
        ...(usuarioControl.errors || {}),
        customError: true
      });

      this.validacionUsuario = this.validacionUsuario
        || 'Debe ingresar un usuario válido.';
    }

    const erroresRegistro = this.obtenerErroresRegistro();

    if (erroresRegistro.length) {
      this.mostrarErroresRegistro(erroresRegistro);
      return;
    }

    this.registrar();
  }

  desbloquearAutofill(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;

    setTimeout(() => {
      inputElement.readOnly = false;
    }, 80);
  }

  bloquearAutofill(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.readOnly = true;
  }

  isInvalid(controlName: string): boolean {
    const control = this.getControl(controlName);

    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getError(controlName: string): string {
    const control = this.getControl(controlName);

    if (!control || !control.errors || !(control.touched || control.dirty)) {
      return '';
    }

    if (control.hasError('required')) {
      return 'El campo es obligatorio';
    }

    if (control.hasError('telefonoLocalIncompleto')) {
      return 'Ingresa los 10 dígitos del teléfono';
    }

    if (control.hasError('email')) {
      return 'El correo no tiene un formato válido';
    }

    if (control.hasError('pattern')) {
      return this.getPatternError(controlName);
    }

    if (control.hasError('minlength') || control.hasError('minlengthCustom')) {
      return 'El valor es demasiado corto';
    }

    if (control.hasError('maxlength') || control.hasError('maxlengthCustom')) {
      return 'El valor excede la longitud permitida';
    }

    if (control.hasError('exists') || control.hasError('customError')) {
      return this.validacionUsuario || 'El usuario no es válido';
    }

    if (control.hasError('formatoIncorrecto')) {
      return 'La contraseña tiene un formato incorrecto';
    }

    if (control.hasError('match')) {
      return 'Las contraseñas no coinciden';
    }

    return 'El campo no es válido';
  }

  abrirErrorCampo(controlName: string): void {
    const mensaje = this.getError(controlName)
      || this.obtenerMensajeErrorControl(controlName)
      || 'El campo no es válido.';

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'error',
        icon: 'error_outline',
        title: `${this.obtenerEtiquetaControl(controlName)} no válido`,
        message: mensaje,
        actionText: 'Entendido'
      }
    });
  }

  abrirErrorPassword(): void {
    if (this.mostrarIconoPasswordError()) {
      this.abrirErroresPassword();
      return;
    }

    this.abrirErrorCampo('Password');
  }

  abrirErroresPassword(): void {
    const validaciones = this.passwordValidaciones.length
      ? this.passwordValidaciones
      : ['No fue posible obtener el detalle de las reglas de contraseña.'];

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'warning',
        icon: 'error_outline',
        title: 'Revisa tu contraseña',
        message: 'La contraseña debe cumplir con las siguientes condiciones:',
        messages: validaciones,
        actionText: 'Entendido'
      }
    });
  }

  abrirInfoUsuario(): void {
    const mensaje = this.validacionUsuario
      || this.getError('Usuario')
      || 'El usuario no es válido.';

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'error',
        icon: 'error_outline',
        title: 'Usuario no disponible',
        message: mensaje,
        actionText: 'Entendido'
      }
    });
  }

  abrirInfoAlias(): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '720px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'info',
        icon: 'info',
        title: 'Alias o sobrenombre',
        message: 'El alias hace referencia a lo que verá al intentar iniciar sesión.',
        imageSrc: 'assets/img/registro/atlas-login-ejemplo.png',
        imageAlt: 'Ejemplo de alias al iniciar sesión',
        actionText: 'Entendido'
      }
    });
  }

  mostrarIconoPasswordError(): boolean {
    const passwordControl = this.todoFormulario.controls.Password;

    return passwordControl.hasError('formatoIncorrecto')
      && (passwordControl.touched || passwordControl.dirty);
  }

  mostrarIconoPasswordCorrecto(): boolean {
    const passwordControl = this.todoFormulario.controls.Password;

    return !!passwordControl.value
      && passwordControl.valid
      && this.passwordValido
      && !this.validandoPassword
      && !this.generaErrorPasswordForm;
  }

  mostrarIconoConfirmaPasswordCorrecto(): boolean {
    const passwordControl = this.todoFormulario.controls.Password;
    const confirmaPasswordControl = this.todoFormulario.controls.ConfirmaPassword;

    return !!passwordControl.value
      && !!confirmaPasswordControl.value
      && passwordControl.value === confirmaPasswordControl.value
      && this.passwordValido
      && !this.validandoPassword
      && !this.generaErrorPasswordForm
      && !confirmaPasswordControl.errors;
  }

  mostrarErrorUsuarioContacto(controlName: ContactoControlName): boolean {
    const control = this.todoFormulario.get(controlName);

    return !!this.errorUsuarioContacto[controlName]
      && !!control?.value
      && control.valid
      && !this.usuarioValido.is;
  }

  getErrorUsuarioContacto(controlName: ContactoControlName): string {
    return this.errorUsuarioContacto[controlName];
  }

  verificarCorreo(): void {
    if (
      this.validandoCorreo
      || this.procesandoCorreo
      || this.twoFaCorreoAbierto
      || this.consultandoNormaCorreo
      || !this.dosFaCorreoHabilitado
    ) {
      return;
    }

    const emailControl = this.todoFormulario.controls.Email;
    emailControl.markAsTouched();

    if (!emailControl.valid) {
      return;
    }

    if (!this.validarUsuarioParaContacto('Email', true)) {
      return;
    }

    if (this.normaCorreoConsultada) {
      this.abrirValidadorDosFaCorreo();
      return;
    }

    this.validandoCorreo = false;
    this.procesandoCorreo = true;
    this.store.dispatch(ActivarLoadingAction());

    this.authService.obtenerNormaAtlas(this.crearNorma(763))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.procesandoCorreo = false;
          this.store.dispatch(DesactivarLoadingAction());
        })
      )
      .subscribe({
        next: (response: IResponseModel) => {
          this.aplicarConfiguracionDosFaCorreo(response);

          if (this.dosFaCorreoHabilitado) {
            this.abrirValidadorDosFaCorreo();
          }
        },
        error: () => {
          this.normaCorreoConsultada = false;
          this.dosFaCorreoHabilitado = true;
          this.reiniciarValidacionCorreo();
          this.abrirDialogoInformativo(
            'error',
            'Error al registrar',
            'Intenta solicitar tu código más tarde.'
          );
        }
      });
  }

  verificarCorreoAlPerderFoco(): void {
    const emailControl = this.todoFormulario.controls.Email;
    emailControl.markAsTouched();

    if (!this.dosFaCorreoHabilitado || this.consultandoNormaCorreo) {
      this.limpiarErrorUsuarioContacto('Email');
      return;
    }

    if (
      this.validandoCorreo
      || this.procesandoCorreo
      || !emailControl.valid
    ) {
      this.limpiarErrorUsuarioContacto('Email');
      return;
    }

    if (!this.validarUsuarioParaContacto('Email', false)) {
      return;
    }

    this.verificarCorreo();
  }

  verificarTelefonoAlPerderFoco(): void {
    this.sincronizarTelefonoCompleto(true);

    if (!this.dosFaTelefonoHabilitado || this.consultandoNormaTelefono) {
      this.limpiarErrorUsuarioContacto('Telefono');
      return;
    }

    if (!this.telefonoTieneFormatoCorrecto()) {
      this.limpiarErrorUsuarioContacto('Telefono');
      return;
    }

    if (!this.validarUsuarioParaContacto('Telefono', false)) {
      return;
    }

    this.verificarTelefono();
  }

  telefonoTieneFormatoCorrecto(): boolean {
    const telefonoControl = this.todoFormulario.controls.Telefono;

    return this.telefonoLocal.length === 10
      && !!this.codigoPaisTelefonoSeleccionado?.codigo
      && telefonoControl.valid;
  }

  telefonoPuedeVerificarse(): boolean {
    return this.telefonoTieneFormatoCorrecto()
      && this.dosFaTelefonoHabilitado
      && !this.consultandoNormaTelefono
      && !this.validandoTelefono
      && !this.procesandoTelefono;
  }

  seleccionarCodigoPaisTelefono(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const codigoPais = this.codigosPaisTelefono.find(
      item => this.getCodigoPaisTelefonoKey(item) === selectElement.value
    );

    if (!codigoPais) {
      return;
    }

    this.codigoPaisTelefonoSeleccionado = codigoPais;
    this.sincronizarTelefonoCompleto(true);
  }

  capturarTelefonoLocal(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const valor = inputElement.value
      .replace(/\D/g, '')
      .slice(0, 10);

    inputElement.value = valor;
    this.telefonoLocal = valor;

    this.sincronizarTelefonoCompleto(true);
  }

  verificarTelefono(): void {
    if (
      this.validandoTelefono
      || this.procesandoTelefono
      || this.twoFaTelefonoAbierto
      || this.consultandoNormaTelefono
      || !this.dosFaTelefonoHabilitado
    ) {
      return;
    }

    const telefonoControl = this.todoFormulario.controls.Telefono;
    telefonoControl.markAsTouched();

    if (!telefonoControl.valid) {
      return;
    }

    if (!this.validarUsuarioParaContacto('Telefono', true)) {
      return;
    }

    if (this.normaTelefonoConsultada) {
      this.abrirValidadorDosFaTelefono();
      return;
    }

    this.validandoTelefono = false;
    this.procesandoTelefono = true;
    this.store.dispatch(ActivarLoadingAction());

    this.authService.obtenerNormaAtlas(this.crearNorma(765))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.procesandoTelefono = false;
          this.store.dispatch(DesactivarLoadingAction());
        })
      )
      .subscribe({
        next: (response: IResponseModel) => {
          this.aplicarConfiguracionDosFaTelefono(response);

          if (this.dosFaTelefonoHabilitado) {
            this.abrirValidadorDosFaTelefono();
          }
        },
        error: () => {
          this.normaTelefonoConsultada = false;
          this.dosFaTelefonoHabilitado = true;
          this.reiniciarValidacionTelefono();
          this.abrirDialogoInformativo(
            'error',
            'Error al registrar',
            'Intenta solicitar tu código más tarde.'
          );
        }
      });
  }

  get codigoPaisTelefonoSeleccionadoKey(): string {
    return this.getCodigoPaisTelefonoKey(this.codigoPaisTelefonoSeleccionado);
  }

  getCodigoPaisTelefonoKey(pais: CodigoPaisTelefono): string {
    if (!pais) {
      return '';
    }

    return `${pais.codigo}|${this.normalizarTexto(pais.pais)}`;
  }

  private configurarValidadoresCuenta(): void {
    const cuentaControl = this.todoFormulario.controls.Cuenta;
    const validadores = [Validators.required];

    if (this.configuracionRegistro().cuentaSoloNumerica) {
      validadores.push(Validators.pattern('^[0-9]*$'));
    }

    cuentaControl.setValidators(validadores);
    cuentaControl.updateValueAndValidity({ emitEvent: false });
  }

  private configurarTipoPersona(): void {
    const tipoPersonaControl = this.todoFormulario.controls.TipoPersona;

    tipoPersonaControl.valueChanges
      .pipe(
        startWith(tipoPersonaControl.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(tipoPersona => {
        if (
          this.tipoPersonaAnterior !== null
          && this.tipoPersonaAnterior !== tipoPersona
        ) {
          this.limpiarDatosPersona();
        }

        this.tipoPersonaAnterior = tipoPersona;
        this.aplicarValidadoresTipoPersona(tipoPersona);
      });
  }

  private aplicarValidadoresTipoPersona(tipoPersona: TipoPersona): void {
    const controlesPersonaFisica = [
      this.todoFormulario.controls.Nombre,
      this.todoFormulario.controls.Paterno,
      this.todoFormulario.controls.Materno
    ];
    const razonSocialControl = this.todoFormulario.controls.RazonSocial;

    if (tipoPersona === 1) {
      controlesPersonaFisica.forEach(control => {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity({ emitEvent: false });
      });

      razonSocialControl.clearValidators();
      razonSocialControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    controlesPersonaFisica.forEach(control => {
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });

    razonSocialControl.setValidators([Validators.required]);
    razonSocialControl.updateValueAndValidity({ emitEvent: false });
  }

  private limpiarDatosPersona(): void {
    this.todoFormulario.controls.Nombre.setValue('', { emitEvent: false });
    this.todoFormulario.controls.Paterno.setValue('', { emitEvent: false });
    this.todoFormulario.controls.Materno.setValue('', { emitEvent: false });
    this.todoFormulario.controls.RazonSocial.setValue('', { emitEvent: false });
  }

  private configurarValidacionUsuario(): void {
    const usuarioControl = this.todoFormulario.controls.Usuario;

    usuarioControl.valueChanges
      .pipe(
        startWith(usuarioControl.value),
        tap(usuario => {
          this.validacionUsuario = '';
          this.usuarioValido.is = false;
          this.usuarioValido.pending = !!`${usuario || ''}`.trim();
          this.limpiarErrorUsuarioContacto();
          this.limpiarErroresControl(usuarioControl, [
            'exists',
            'customError',
            'minlengthCustom',
            'maxlengthCustom'
          ]);
        }),
        debounceTime(800),
        distinctUntilChanged(),
        switchMap(usuario => this.evaluarUsuario(`${usuario || ''}`)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(resultado => {
        this.aplicarResultadoValidacionUsuario(resultado);
      });
  }

  private evaluarUsuario(usuario: string): Observable<ResultadoValidacionUsuario> {
    const valorUsuario = usuario.trim();

    if (!valorUsuario) {
      return of({ estado: 'vacio' });
    }

    if (!/^[a-zA-Z0-9_]*$/.test(valorUsuario)) {
      return of({
        estado: 'invalido-local',
        mensaje: 'Solo se permiten letras, números y guion bajo.'
      });
    }

    if (valorUsuario.length < 4) {
      return of({
        estado: 'invalido-local',
        mensaje: 'El nombre de usuario debe tener al menos 4 caracteres.'
      });
    }

    if (valorUsuario.length > 8) {
      return of({
        estado: 'invalido-local',
        mensaje: 'El campo solo puede tener hasta 8 caracteres.'
      });
    }

    return this.authService.validaUsuario(valorUsuario).pipe(
      switchMap(() =>
        this.authService.validaUsuarioSisa(valorUsuario).pipe(
          map((): ResultadoValidacionUsuario => ({ estado: 'valido' })),
          catchError(error => of<ResultadoValidacionUsuario>({
            estado: 'existente',
            mensaje: 'La clave de usuario ya existe.',
            error
          }))
        )
      ),
      catchError(error => of<ResultadoValidacionUsuario>({
        estado: 'invalido-remoto',
        mensaje: this.obtenerMensajeValidacionUsuario(error),
        error
      }))
    );
  }

  private aplicarResultadoValidacionUsuario(
    resultado: ResultadoValidacionUsuario
  ): void {
    const usuarioControl = this.todoFormulario.controls.Usuario;
    this.usuarioValido.pending = false;

    if (resultado.estado === 'vacio') {
      this.usuarioValido.is = false;
      return;
    }

    if (resultado.estado === 'valido') {
      this.usuarioValido.is = true;
      this.validacionUsuario = '';

      if (this.validandoCorreo || this.validandoTelefono) {
        this.actualizarUsuario = 'true';
      }

      this.limpiarErroresControl(usuarioControl, [
        'exists',
        'customError',
        'minlengthCustom',
        'maxlengthCustom',
        'pattern'
      ]);
      return;
    }

    this.usuarioValido.is = false;
    this.validacionUsuario = resultado.mensaje || 'Usuario inválido.';

    if (resultado.estado === 'existente') {
      usuarioControl.setErrors({
        ...(usuarioControl.errors || {}),
        exists: true,
        customError: true
      });
      return;
    }

    if (resultado.estado === 'invalido-remoto') {
      usuarioControl.setErrors({
        ...(usuarioControl.errors || {}),
        customError: true
      });
    }
  }

  private configurarValidacionPassword(): void {
    const passwordControl = this.todoFormulario.controls.Password;
    const usuarioControl = this.todoFormulario.controls.Usuario;

    combineLatest([
      passwordControl.valueChanges.pipe(startWith(passwordControl.value)),
      usuarioControl.valueChanges.pipe(startWith(usuarioControl.value))
    ])
      .pipe(
        tap(([password]) => {
          if (`${password || ''}`.trim()) {
            this.validandoPassword = true;
            this.passwordValido = false;
          }
        }),
        debounceTime(800),
        distinctUntilChanged(
          ([passwordAnterior, usuarioAnterior], [passwordActual, usuarioActual]) =>
            passwordAnterior === passwordActual && usuarioAnterior === usuarioActual
        ),
        switchMap(([password, usuario]) =>
          this.evaluarPassword(`${password || ''}`, `${usuario || ''}`)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(resultado => {
        this.aplicarResultadoValidacionPassword(resultado);
      });
  }

  private evaluarPassword(
    password: string,
    usuario: string
  ): Observable<ResultadoValidacionPassword> {
    if (!password.trim()) {
      return of({ estado: 'vacio' });
    }

    return this.adminService.postValidaContrasena({ password, usuario }).pipe(
      map((respuesta: IResponseValidaContrasena): ResultadoValidacionPassword => {
        if (respuesta?.datos?.length > 0) {
          return {
            estado: 'invalido',
            respuesta
          };
        }

        return {
          estado: 'valido',
          respuesta
        };
      }),
      catchError(error => of<ResultadoValidacionPassword>({
        estado: 'error',
        error
      }))
    );
  }

  private aplicarResultadoValidacionPassword(
    resultado: ResultadoValidacionPassword
  ): void {
    const passwordControl = this.todoFormulario.controls.Password;
    this.validandoPassword = false;

    if (resultado.estado === 'vacio') {
      this.reiniciarValidacionesContrasena();
      return;
    }

    if (resultado.estado === 'valido') {
      this.generaErrorPasswordForm = false;
      this.passwordValido = true;
      this.passwordValidaciones = [];
      this.limpiarErroresControl(passwordControl, ['formatoIncorrecto']);
      this.sincronizarErrorConfirmaPassword();
      return;
    }

    this.generaErrorPasswordForm = true;
    this.passwordValido = false;
    passwordControl.setErrors({
      ...(passwordControl.errors || {}),
      formatoIncorrecto: true
    });

    if (resultado.estado === 'invalido') {
      this.passwordValidaciones = this.normalizarMensajesPassword(
        resultado.respuesta?.datos
      );
    } else {
      this.passwordValidaciones = this.normalizarMensajesPassword(
        this.obtenerErrorDesconocido(resultado.error)
      );
    }

    if (!this.passwordValidaciones.length) {
      this.passwordValidaciones = [
        this.obtenerMensajeErrorPassword(resultado.error)
      ];
    }

    this.sincronizarErrorConfirmaPassword();
  }

  private configurarSincronizacionConfirmacionPassword(): void {
    const passwordControl = this.todoFormulario.controls.Password;
    const confirmaPasswordControl = this.todoFormulario.controls.ConfirmaPassword;

    combineLatest([
      passwordControl.valueChanges.pipe(startWith(passwordControl.value)),
      confirmaPasswordControl.valueChanges.pipe(startWith(confirmaPasswordControl.value))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sincronizarErrorConfirmaPassword());
  }

  private sincronizarErrorConfirmaPassword(): void {
    const passwordControl = this.todoFormulario.controls.Password;
    const confirmaPasswordControl = this.todoFormulario.controls.ConfirmaPassword;

    if (!confirmaPasswordControl.value) {
      this.limpiarErroresControl(confirmaPasswordControl, ['match']);
      return;
    }

    if (passwordControl.value !== confirmaPasswordControl.value) {
      confirmaPasswordControl.setErrors({
        ...(confirmaPasswordControl.errors || {}),
        match: true
      });
      return;
    }

    this.limpiarErroresControl(confirmaPasswordControl, ['match']);
  }

  private configurarResetValidacionContacto(): void {
    this.todoFormulario.controls.Email.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(email => {
        if (
          this.validandoCorreo
          && email !== this.emailValidado
          && !this.confirmandoCambioCorreo
        ) {
          this.confirmarCambioEmail();
        }
      });

    this.todoFormulario.controls.Telefono.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(telefono => {
        if (
          this.validandoTelefono
          && `${telefono || ''}` !== this.telefonoValidado
          && !this.confirmandoCambioTelefono
        ) {
          this.confirmarCambioTelefono();
        }
      });
  }

  private confirmarCambioEmail(): void {
    this.confirmandoCambioCorreo = true;

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'warning',
        icon: 'error_outline',
        title: 'Cambiar email',
        message: 'Si cambias el email tendrás que validarlo nuevamente. ¿Deseas continuar?',
        showCancelButton: true,
        confirmText: 'Continuar',
        cancelText: 'Cancelar'
      }
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmado: boolean) => {
        this.confirmandoCambioCorreo = false;

        if (confirmado) {
          this.reiniciarValidacionCorreo();
          return;
        }

        this.todoFormulario.controls.Email.setValue(this.emailValidado, {
          emitEvent: false
        });
      });
  }

  private confirmarCambioTelefono(): void {
    this.confirmandoCambioTelefono = true;

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'warning',
        icon: 'error_outline',
        title: 'Cambiar teléfono',
        message: 'Si cambias el teléfono tendrás que validarlo nuevamente. ¿Deseas continuar?',
        showCancelButton: true,
        confirmText: 'Continuar',
        cancelText: 'Cancelar'
      }
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmado: boolean) => {
        this.confirmandoCambioTelefono = false;

        if (confirmado) {
          this.reiniciarValidacionTelefono();
          return;
        }

        this.sincronizarTelefonoVisualDesdeCompleto(this.telefonoValidado);
        this.todoFormulario.controls.Telefono.setValue(this.telefonoValidado, {
          emitEvent: false
        });
      });
  }

  private validarUsuarioParaContacto(
    controlName: ContactoControlName,
    mostrarDialogo = false
  ): boolean {
    const usuarioControl = this.todoFormulario.controls.Usuario;
    const usuario = usuarioControl.value.trim();

    if (this.usuarioValido.is && !this.usuarioValido.pending) {
      this.limpiarErrorUsuarioContacto(controlName);
      return true;
    }

    const mensaje = !usuario
      ? 'Primero ingresa un usuario para poder verificar email o teléfono.'
      : this.usuarioValido.pending
        ? 'Espera a que termine la validación del usuario para verificar email o teléfono.'
        : 'Primero ingresa un usuario válido para poder verificar email o teléfono.';

    this.errorUsuarioContacto[controlName] = mensaje;
    usuarioControl.markAsTouched();

    if (!usuario) {
      usuarioControl.setErrors({
        ...(usuarioControl.errors || {}),
        required: true
      });
    }

    if (mostrarDialogo) {
      this.abrirDialogoInformativo(
        'warning',
        'Usuario requerido',
        mensaje
      );
    }

    return false;
  }

  private limpiarErrorUsuarioContacto(
    controlName?: ContactoControlName
  ): void {
    if (controlName) {
      this.errorUsuarioContacto[controlName] = '';
      return;
    }

    this.errorUsuarioContacto.Email = '';
    this.errorUsuarioContacto.Telefono = '';
  }

  private abrirValidadorDosFaCorreo(): void {
    if (this.twoFaCorreoAbierto) {
      return;
    }

    this.twoFaCorreoAbierto = true;

    const configModal: MatDialogConfig = {
      autoFocus: false,
      disableClose: true,
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'registro-twofa-dialog-panel',
      data: {
        tipo: 'Correo Electronico',
        valor: this.todoFormulario.controls.Email.value,
        usuario: this.todoFormulario.controls.Usuario.value
      }
    };

    this.dialog.open(RegistroValidadorDosFaComponent, configModal)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { TwoFactorCode?: string } | undefined) => {
        this.twoFaCorreoAbierto = false;
        const codigo = result?.TwoFactorCode || '';

        if (codigo) {
          this.marcarCorreoValidado(codigo);
          return;
        }

        this.reiniciarValidacionCorreo();
      });
  }

  private abrirValidadorDosFaTelefono(): void {
    if (this.twoFaTelefonoAbierto) {
      return;
    }

    this.twoFaTelefonoAbierto = true;

    const configModal: MatDialogConfig = {
      autoFocus: false,
      disableClose: true,
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'registro-twofa-dialog-panel',
      data: {
        tipo: 'Telefono',
        valor: this.todoFormulario.controls.Telefono.value,
        usuario: this.todoFormulario.controls.Usuario.value
      }
    };

    this.dialog.open(RegistroValidadorDosFaComponent, configModal)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { TwoFactorCode?: string } | undefined) => {
        this.twoFaTelefonoAbierto = false;
        const codigo = result?.TwoFactorCode || '';

        if (codigo) {
          this.marcarTelefonoValidado(codigo);
          return;
        }

        this.reiniciarValidacionTelefono();
      });
  }

  private marcarCorreoValidado(codigo: string): void {
    this.todoFormulario.controls.CodEmail.setValue(codigo);
    this.validandoCorreo = true;
    this.emailValidado = this.todoFormulario.controls.Email.value;

    if (this.validandoTelefono) {
      this.actualizarUsuario = 'false';
    }
  }

  private marcarTelefonoValidado(codigo: string): void {
    this.todoFormulario.controls.CodTelefono.setValue(codigo);
    this.validandoTelefono = true;
    this.telefonoValidado = `${this.todoFormulario.controls.Telefono.value || ''}`;
    this.sincronizarTelefonoVisualDesdeCompleto(this.telefonoValidado);

    if (this.validandoCorreo) {
      this.actualizarUsuario = 'false';
    }
  }

  private reiniciarValidacionCorreo(): void {
    this.todoFormulario.controls.CodEmail.setValue(
      this.dosFaCorreoHabilitado ? '' : '9000'
    );
    this.validandoCorreo = false;
    this.emailValidado = '';
  }

  private reiniciarValidacionTelefono(): void {
    this.todoFormulario.controls.CodTelefono.setValue(
      this.dosFaTelefonoHabilitado ? '' : '9000'
    );
    this.validandoTelefono = false;
    this.telefonoValidado = '';
  }

  private sincronizarTelefonoCompleto(markTouched = false): void {
    const telefonoControl = this.todoFormulario.controls.Telefono;
    const telefonoCompleto = this.telefonoLocal
      ? `${this.codigoPaisTelefonoSeleccionado.codigo}${this.telefonoLocal}`
      : '';

    telefonoControl.setValue(telefonoCompleto, { emitEvent: true });

    if (markTouched) {
      telefonoControl.markAsDirty();
      telefonoControl.markAsTouched();
    }

    telefonoControl.updateValueAndValidity({
      onlySelf: true,
      emitEvent: false
    });

    if (markTouched && this.telefonoLocal && this.telefonoLocal.length !== 10) {
      telefonoControl.setErrors({
        ...(telefonoControl.errors || {}),
        telefonoLocalIncompleto: true
      });
    }
  }

  private sincronizarTelefonoVisualDesdeCompleto(
    telefonoCompleto: string
  ): void {
    const valor = `${telefonoCompleto || ''}`.replace(/\D/g, '');

    if (!valor) {
      this.codigoPaisTelefonoSeleccionado = this.codigosPaisTelefono[0];
      this.telefonoLocal = '';
      return;
    }

    const codigoEncontrado = [...this.codigosPaisTelefono]
      .sort((a, b) => b.codigo.length - a.codigo.length)
      .find(item => valor.startsWith(item.codigo));

    this.codigoPaisTelefonoSeleccionado = codigoEncontrado
      || this.codigosPaisTelefono[0];

    this.telefonoLocal = valor
      .slice(this.codigoPaisTelefonoSeleccionado.codigo.length)
      .slice(0, 10);
  }

  private cargarCatalogoPreguntas(): void {
    this.authService.catalogoPreguntas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.catPreguntas = Array.isArray(response)
            ? response as CatalogoPregunta[]
            : [];
        },
        error: () => {
          this.catPreguntas = [];
        }
      });
  }

  private cargarLadasTelefonicasInternacionales(): void {
    this.store.dispatch(ActivarLoadingAction());

    this.authService.obtenerLadasTelefonicasInternacionales()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.store.dispatch(DesactivarLoadingAction()))
      )
      .subscribe({
        next: (response: any) => {
          const ladas = Array.isArray(response?.Respuesta)
            ? response.Respuesta
            : [];

          const codigosPais = ladas
            .filter((item: LadaTelefonicaInternacionalResponse) =>
              item?.valor_dato !== null
              && item?.valor_dato !== undefined
              && !!item?.descripl_dato
            )
            .map((item: LadaTelefonicaInternacionalResponse) => ({
              codigo: `${item.valor_dato}`.trim(),
              pais: `${item.descripl_dato}`.trim(),
              bandera: this.obtenerClavePais(`${item.descripl_dato}`)
            }));

          if (!codigosPais.length) {
            this.seleccionarMexicoComoLadaInicial();
            return;
          }

          this.codigosPaisTelefono = this.ordenarLadasTelefono(codigosPais);
          this.seleccionarMexicoComoLadaInicial();
          this.sincronizarTelefonoCompleto(false);
        },
        error: () => {
          this.seleccionarMexicoComoLadaInicial();
        }
      });
  }

  private ordenarLadasTelefono(
    ladas: CodigoPaisTelefono[]
  ): CodigoPaisTelefono[] {
    return [...ladas].sort((a, b) => {
      if (this.esMexicoLada(a)) {
        return -1;
      }

      if (this.esMexicoLada(b)) {
        return 1;
      }

      return this.normalizarTexto(a.pais).localeCompare(
        this.normalizarTexto(b.pais)
      );
    });
  }

  private esMexicoLada(lada: CodigoPaisTelefono): boolean {
    return lada.codigo === '52'
      || this.normalizarTexto(lada.pais) === 'mexico';
  }

  private seleccionarMexicoComoLadaInicial(): void {
    const mexico = this.codigosPaisTelefono.find(item => this.esMexicoLada(item));
    this.codigoPaisTelefonoSeleccionado = mexico || this.codigosPaisTelefono[0];
  }

  private registrar(): void {
    if (this.loadingRegistro) {
      return;
    }

    this.todoFormulario.controls.ValidarUsuario.setValue(
      this.actualizarUsuario
    );

    const payload = this.todoFormulario.getRawValue();

    this.loadingRegistro = true;
    this.store.dispatch(ActivarLoadingAction());

    this.ejecutarRegistro(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingRegistro = false;
          this.store.dispatch(DesactivarLoadingAction());
        })
      )
      .subscribe({
        next: () => {
          this.registroCompletado.emit(
            this.todoFormulario.controls.Usuario.value
          );
        },
        error: (error: HttpErrorResponse) => {
          this.abrirDialogoInformativo(
            'error',
            'Error al registrar',
            this.obtenerMensajeErrorRegistro(error)
          );
        }
      });
  }

  private ejecutarRegistro(
    payload: RegistroProveedorPromotorPayload
  ): Observable<unknown> {
    return this.tipoRegistro() === 'Proveedor'
      ? this.authService.registrarProveedor(payload)
      : this.authService.registrarPromotor(payload);
  }

  private obtenerErroresRegistro(): string[] {
    const errores = this.obtenerErroresFormulario();

    if (this.usuarioValido.pending) {
      errores.push('Usuario: La validación del usuario aún está en proceso.');
    }

    if (!this.usuarioValido.is) {
      errores.push(
        `Usuario: ${this.validacionUsuario || 'Debe ingresar un usuario válido.'}`
      );
    }

    if (this.validandoPassword) {
      errores.push('Contraseña: La validación de contraseña aún está en proceso.');
    }

    if (
      (this.generaErrorPasswordForm || !this.passwordValido)
      && !!this.todoFormulario.controls.Password.value
    ) {
      errores.push('Contraseña: La contraseña tiene un formato incorrecto.');
    }

    if (this.consultandoNormaCorreo) {
      errores.push('Email: La configuración de verificación aún está en proceso.');
    } else if (this.dosFaCorreoHabilitado && !this.validandoCorreo) {
      errores.push('Email: El email no está validado.');
    }

    if (this.consultandoNormaTelefono) {
      errores.push('Teléfono celular: La configuración de verificación aún está en proceso.');
    } else if (this.dosFaTelefonoHabilitado && !this.validandoTelefono) {
      errores.push('Teléfono celular: El teléfono celular no está validado.');
    }

    return errores.filter(
      (error, index, arreglo) => arreglo.indexOf(error) === index
    );
  }

  private obtenerErroresFormulario(): string[] {
    const errores: string[] = [];

    Object.keys(this.todoFormulario.controls).forEach(controlName => {
      if (['CodEmail', 'CodTelefono', 'ValidarUsuario'].includes(controlName)) {
        return;
      }

      const control = this.getControl(controlName);

      if (!control?.errors) {
        return;
      }

      const mensaje = this.obtenerMensajeErrorControl(controlName);

      if (mensaje) {
        errores.push(`${this.obtenerEtiquetaControl(controlName)}: ${mensaje}`);
      }
    });

    return errores;
  }

  private obtenerMensajeErrorControl(controlName: string): string {
    const control = this.getControl(controlName);

    if (!control?.errors) {
      return '';
    }

    if (control.hasError('required')) {
      return 'El campo es obligatorio.';
    }

    if (control.hasError('telefonoLocalIncompleto')) {
      return 'Ingresa los 10 dígitos del teléfono.';
    }

    if (control.hasError('email')) {
      return 'El correo no tiene un formato válido.';
    }

    if (control.hasError('pattern')) {
      return this.getPatternError(controlName);
    }

    if (control.hasError('minlength') || control.hasError('minlengthCustom')) {
      return 'El valor es demasiado corto.';
    }

    if (control.hasError('maxlength') || control.hasError('maxlengthCustom')) {
      return 'El valor excede la longitud permitida.';
    }

    if (control.hasError('exists') || control.hasError('customError')) {
      return this.validacionUsuario || 'El usuario no es válido.';
    }

    if (control.hasError('formatoIncorrecto')) {
      return 'La contraseña tiene un formato incorrecto.';
    }

    if (control.hasError('match')) {
      return 'Las contraseñas no coinciden.';
    }

    return 'El campo no es válido.';
  }

  private obtenerEtiquetaControl(controlName: string): string {
    const etiquetas: Record<string, string> = {
      NIP: 'NIP',
      Usuario: 'Usuario',
      Cuenta: this.configuracionRegistro().etiquetaCuenta,
      Password: 'Contraseña',
      ConfirmaPassword: 'Confirma contraseña',
      Alias: 'Alias o sobrenombre',
      Telefono: 'Teléfono celular',
      Email: 'Email',
      IdPregunta: 'Pregunta secreta',
      Respuesta: 'Respuesta',
      TipoPersona: 'Tipo de persona',
      Nombre: 'Nombre',
      Paterno: 'Apellido paterno',
      Materno: 'Apellido materno',
      RazonSocial: 'Razón social',
      RFC: 'RFC'
    };

    return etiquetas[controlName] || controlName;
  }

  private mostrarErroresRegistro(errores: string[]): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'error',
        icon: 'error_outline',
        title: 'Revisa la información',
        message: 'No fue posible continuar con el registro. Corrige lo siguiente:',
        messages: errores,
        actionText: 'Entendido'
      }
    });
  }

  private abrirDialogoInformativo(
    type: 'info' | 'warning' | 'error' | 'success',
    title: string,
    message: string
  ): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type,
        title,
        message,
        actionText: 'Aceptar'
      }
    });
  }

  private obtenerMensajeErrorRegistro(error: HttpErrorResponse): string {
    const body = error?.error;

    if (body?.Message) {
      return body.Message;
    }

    if (Array.isArray(body)) {
      const errores = body
        .map(item => item?.Exception || item?.Message || item?.message)
        .filter(Boolean);

      if (errores.length) {
        return errores.join('<br>');
      }
    }

    if (body?.Exception) {
      return body.Exception;
    }

    if (typeof body === 'string') {
      return body;
    }

    return 'No fue posible registrar el usuario. Intenta nuevamente.';
  }

  private obtenerMensajeValidacionUsuario(error: unknown): string {
    const body = this.obtenerErrorDesconocido(error);

    return body?.Exception
      || body?.Message
      || body?.message
      || 'Usuario inválido.';
  }

  private obtenerMensajeErrorPassword(error: unknown): string {
    const body = this.obtenerErrorDesconocido(error);
    const mensajes = this.normalizarMensajesPassword(body);

    return mensajes.join(' ') || 'Ups, se generó un error.';
  }

  private normalizarMensajesPassword(validaciones: unknown): string[] {
    if (!validaciones) {
      return [];
    }

    if (typeof validaciones === 'string') {
      return [validaciones];
    }

    if (!Array.isArray(validaciones)) {
      return this.normalizarMensajesPassword([validaciones]);
    }

    return validaciones
      .map((validacion: any) => {
        if (typeof validacion === 'string') {
          return validacion;
        }

        return validacion?.Descripcion
          || validacion?.descripcion
          || validacion?.Description
          || validacion?.description
          || validacion?.Exception
          || validacion?.exception
          || validacion?.Mensaje
          || validacion?.mensaje
          || validacion?.Message
          || validacion?.message
          || validacion?.password?.[0]
          || '';
      })
      .filter((mensaje: string) => !!mensaje && mensaje.trim().length > 0);
  }

  private reiniciarValidacionesContrasena(): void {
    this.generaErrorPasswordForm = false;
    this.validandoPassword = false;
    this.passwordValido = false;
    this.passwordValidaciones = [];

    this.limpiarErroresControl(
      this.todoFormulario.controls.Password,
      ['formatoIncorrecto']
    );
  }

  private limpiarErroresControl(
    control: AbstractControl,
    errores: string[]
  ): void {
    if (!control.errors) {
      return;
    }

    const erroresActualizados = { ...control.errors };

    errores.forEach(errorKey => {
      delete erroresActualizados[errorKey];
    });

    control.setErrors(
      Object.keys(erroresActualizados).length ? erroresActualizados : null
    );
  }

  private getPatternError(controlName: string): string {
    switch (controlName) {
      case 'Usuario':
        return 'Solo se permiten letras, números y guion bajo.';
      case 'Cuenta':
        return 'Solo se aceptan números.';
      case 'Telefono':
        return 'Ingresa los 10 dígitos del teléfono.';
      case 'Email':
        return 'El correo no tiene un formato válido.';
      case 'RFC':
        return 'El RFC no tiene un formato válido.';
      default:
        return 'El formato no es válido.';
    }
  }

  private getControl(controlName: string): AbstractControl | null {
    return this.todoFormulario.get(controlName);
  }

  private obtenerErrorDesconocido(error: unknown): any {
    if (error instanceof HttpErrorResponse) {
      return error.error;
    }

    if (typeof error === 'object' && error !== null && 'error' in error) {
      return (error as { error: unknown }).error;
    }

    return error;
  }

  private obtenerClavePais(pais: string): string {
    const paisNormalizado = this.normalizarTexto(pais);

    const clavesPais: Record<string, string> = {
      'estados unidos': 'US',
      'canada': 'CA',
      'mexico': 'MX',
      'brasil': 'BR',
      'argentina': 'AR',
      'colombia': 'CO',
      'chile': 'CL',
      'venezuela': 'VE',
      'peru': 'PE',
      'ecuador': 'EC',
      'cuba': 'CU',
      'bolivia': 'BO',
      'costa rica': 'CR',
      'panama': 'PA',
      'uruguay': 'UY',
      'espana': 'ES',
      'alemania': 'DE',
      'francia': 'FR',
      'italia': 'IT',
      'reino unido': 'GB',
      'rusia': 'RU',
      'ucrania': 'UA',
      'polonia': 'PL',
      'paises bajos': 'NL',
      'belgica': 'BE',
      'portugal': 'PT',
      'china': 'CN',
      'india': 'IN',
      'japon': 'JP',
      'corea del sur': 'KR',
      'australia': 'AU',
      'nueva zelanda': 'NZ'
    };

    return clavesPais[paisNormalizado]
      || pais.trim().substring(0, 2).toUpperCase();
  }

  private normalizarTexto(valor: string): string {
    return `${valor || ''}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
